import type {
  ChoghadiyaPeriod,
  ChoghadiyaResponse,
  ChoghadiyaTimePeriod,
  ChoghadiyaVara,
  PanchangPlanet,
  PanchangResponse,
} from "@ishvaram/api-client";

import { edgeRead } from "./_fetch";

export type {
  ChoghadiyaPeriod,
  ChoghadiyaResponse,
  ChoghadiyaTimePeriod,
  ChoghadiyaVara,
  PanchangPlanet,
  PanchangResponse,
};

export const IST_TZ_OFFSET_HOURS = 5.5;

export async function getPanchang(params: {
  date?: string;
  latitude?: number;
  longitude?: number;
  /** Offset from UTC in hours. India is 5.5, not 330 minutes. */
  tz_offset?: number;
}): Promise<PanchangResponse> {
  const q = new URLSearchParams();
  if (params.date) q.set("date", params.date);
  if (params.latitude !== undefined) q.set("latitude", String(params.latitude));
  if (params.longitude !== undefined) q.set("longitude", String(params.longitude));
  if (params.tz_offset !== undefined) q.set("tz_offset", String(params.tz_offset));
  return edgeRead<PanchangResponse>("panchang", q.toString());
}

export async function fetchChoghadiya(params: {
  date?: string;
  latitude?: number;
  longitude?: number;
  /** Offset from UTC in hours. India is 5.5, not 330 minutes. */
  tz_offset?: number;
}): Promise<ChoghadiyaResponse> {
  const q = new URLSearchParams();
  if (params.date) q.set("date", params.date);
  if (params.latitude !== undefined) q.set("latitude", String(params.latitude));
  if (params.longitude !== undefined) q.set("longitude", String(params.longitude));
  if (params.tz_offset !== undefined) q.set("tz_offset", String(params.tz_offset));
  return edgeRead<ChoghadiyaResponse>("choghadiya", q.toString());
}

/**
 * Chart-personalized daily timing for a saved kundali reading (ADR-0027).
 * `readingId` is the bearer-capability uuid from a saved kundali; the backend
 * recomputes the chart from the stored birth details (engine is SoT, rule 29)
 * and fuses shadbala × dasha × transits × panchang into one daily verdict.
 * Locally typed because the backend PersonalPanchangResponse is not yet exported
 * by @ishvaram/api-client (run `npm run generate-types` in the package to adopt
 * the generated shape later).
 */
export interface PersonalPanchangRemedy {
  action: string;
  reason: string;
  sku_hint?: string | null;
}

/** One Choghadiya window. `quality` is best | good | neutral | bad (engine vocab). */
export interface ChoghadiyaPeriodLite {
  name: string;
  name_hi?: string;
  quality: "best" | "good" | "neutral" | "bad" | string;
  start_time: string; // HH:MM in the response's tz_offset
  end_time: string; // HH:MM in the response's tz_offset
}

export interface PersonalPanchangWindow {
  start?: string | null;
  end?: string | null;
}

export interface PersonalPanchangTiming {
  sunrise?: string | null;
  sunset?: string | null;
  day_choghadiya: ChoghadiyaPeriodLite[];
  night_choghadiya: ChoghadiyaPeriodLite[];
  rahu_kaal: PersonalPanchangWindow;
  abhijit: PersonalPanchangWindow;
}

export interface PersonalPanchangResponse {
  for_date: string;
  overall_score: number;
  overall_verdict: string;
  headline: string;
  summary: string;
  shared_panchang: Record<string, unknown>;
  personal: Record<string, unknown>;
  good_for: string[];
  avoid: string[];
  remedy_of_the_day: PersonalPanchangRemedy;
  best_muhurat_window?: Record<string, unknown> | null;
  score_breakdown: Record<string, unknown>;
  // ADR-0027 live-countdown hero. Optional — older backends omit them.
  latitude?: number | null;
  longitude?: number | null;
  tz_offset?: number | null;
  timing?: PersonalPanchangTiming | null;
}

export async function getPersonalPanchang(
  readingId: string,
  forDate?: string
): Promise<PersonalPanchangResponse> {
  const q = new URLSearchParams();
  if (forDate) q.set("for_date", forDate);
  return edgeRead<PersonalPanchangResponse>(
    `panchang/personal/${encodeURIComponent(readingId)}`,
    q.toString()
  );
}
